/// <amd-module name="Environments/EnvironmentDetails/ActionsCreators/EnvironmentDeploymentsHistoryActionsCreator" />
import { TaskResult, TaskOrchestrationOwner } from "DistributedTask/Client/Contracts/TaskAgent";

import { IEnvironmentDeploymentHistoryService } from "Environments/Client/EnvironmentDeploymentHistoryService";
import { IEnvironmentDeploymentHistoryResult } from "Environments/Client/EnvironmentContracts";
import { EnvironmentDeploymentsHistoryActions } from "Environments/EnvironmentDetails/Actions/EnvironmentDeploymentsHistoryActions";
import { PageKey, getErrorMessage } from "Environments/EnvironmentDetails/Common/Utils";
import { MessagesActions, IMessagePayload } from "Environments/EnvironmentsCommon/MessagesActions";
import { ActionCreatorBase } from "Environments/Flux/ActionCreatorBase";
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";

import { IVssPageContext } from "VSS/Platform/Context";

export class EnvironmentDeploymentsHistoryActionsCreator extends ActionCreatorBase {

    public static getKey(): string {
        return "env-deployments-history-action-creator";
    }

    public initialize(instanceId?: string) {
        super.initialize(instanceId);
        
        this._actions = ActionsHubManager.CreateActionsHub<EnvironmentDeploymentsHistoryActions>(EnvironmentDeploymentsHistoryActions, instanceId);
        this._messageActions = ActionsHubManager.CreateActionsHub<MessagesActions>(MessagesActions, instanceId);
    }

    // Returns true when the information is found in pageData and call completes synchronously.
    // Returns false when API call is being made to fetch the data asynchronously.
    public getEnvironmentDeploymentExecutionRecords(
        pageContext: IVssPageContext,
        environmentId: number,
        resourceId?: number,
        pipelineDefinitionIdFilter?: number,
        statusFilter?: TaskResult,
        forceRefresh: boolean = true): boolean {

        const historyService = pageContext
            .getService<IEnvironmentDeploymentHistoryService>("IEnvironmentDeploymentHistoryService");
 
        if (!forceRefresh) {

            let historyResult: IEnvironmentDeploymentHistoryResult | undefined = historyService
            .getEnvironmentDeploymentExecutionRecords(
                environmentId,
                resourceId,
                pipelineDefinitionIdFilter,
                statusFilter,
                undefined);
            if (!!historyResult) {
                this._actions.updateExecutionRecords.invoke(historyResult);
                // sync
                return true;
            }
        }

        const action = (historyResult: IEnvironmentDeploymentHistoryResult): void => {
            this._actions.updateExecutionRecords.invoke(historyResult);
        }

        this._fetchDeploymentExecutionRecordsAsync(
            pageContext,
            action,
            environmentId,
            resourceId,
            pipelineDefinitionIdFilter,
            statusFilter,
            undefined
        );

        // async
        return false;
    }

    public fetchMoreEnvironmentDeploymentExecutionRecords(
        pageContext: IVssPageContext,
        environmentId: number,
        resourceId?: number,
        pipelineDefinitionIdFilter?: number,
        statusFilter?: TaskResult,
        continuationToken?: number): void {

        const action = (historyResult: IEnvironmentDeploymentHistoryResult): void => {
            this._actions.appendExecutionRecords.invoke(historyResult);
        }

        this._fetchDeploymentExecutionRecordsAsync(
            pageContext,
            action,
            environmentId,
            resourceId,
            pipelineDefinitionIdFilter,
            statusFilter,
            continuationToken
        );
    }

    public _fetchDeploymentExecutionRecordsAsync(
        pageContext: IVssPageContext,
        action: (result: IEnvironmentDeploymentHistoryResult) => void,
        environmentId: number,
        resourceId?: number,
        pipelineDefinitionIdFilter?: number,
        statusFilter?: TaskResult,
        continuationToken?: number): void {

        const historyService = pageContext
            .getService<IEnvironmentDeploymentHistoryService>("IEnvironmentDeploymentHistoryService");
        
        let promise: Promise<IEnvironmentDeploymentHistoryResult | undefined> = historyService
            .getEnvironmentDeploymentExecutionRecordsAsync(
                environmentId,
                resourceId,
                pipelineDefinitionIdFilter,
                statusFilter,
                continuationToken);

        promise.then((historyResult: IEnvironmentDeploymentHistoryResult) => {
            action(historyResult);
        }, (error) => {
            this._raiseError(error);
        });
    }

    public getLinkedPipelines(
        pageContext: IVssPageContext,
        environmentId: number): void {

        const historyService = pageContext
            .getService<IEnvironmentDeploymentHistoryService>("IEnvironmentDeploymentHistoryService");
        
        let promise: Promise<TaskOrchestrationOwner[] | undefined> = historyService
            .getLinkedPipelineDefinitions(environmentId);

        promise.then((pipelineDefinitions: TaskOrchestrationOwner[]) => {
            this._actions.updatePipelineDefinitions.invoke(pipelineDefinitions);
        }, (error) => {
            this._raiseError(error);
        });
    }

    private _raiseError(error: any) {
        const payload: IMessagePayload = { 
            key: PageKey.EnvironmentDeploymentHistory,
            value: getErrorMessage(error) 
        };
        
        this._messageActions
            .addMessage
            .invoke(payload);
    }

    protected disposeInternal(): void {
        ActionsHubManager.DeleteActionsHub<EnvironmentDeploymentsHistoryActions>(EnvironmentDeploymentsHistoryActions, this.getInstanceId());
        ActionsHubManager.DeleteActionsHub<MessagesActions>(MessagesActions, this.getInstanceId());
    }

    private _actions: EnvironmentDeploymentsHistoryActions;
    private _messageActions: MessagesActions;
}
